/**
 * 简化的音频播放器工具类
 * 用于播放从服务器接收到的PCM音频数据
 */
class SimpleAudioPlayer {
  constructor(options = {}) {
    this.sampleRate = options.sampleRate || 22050;
    this.channels = options.channels || 1;
    this.volume = options.volume || 1;
    this.playbackRate = options.playbackRate || 0.85;
    this.fadeDuration = options.fadeDuration || 50;
    this.filterFrequency = options.filterFrequency || 8000;
    this.smallChunkThreshold = options.smallChunkThreshold || 5000;

    this.audioContext = null;
    this.gainNode = null;
    this.filterNode = null;
    this.scriptNode = null;

    this.isPlaying = false;
    this.isPaused = false;
    this.isCompleted = false;

    this.onStatusUpdate = options.onStatusUpdate || (() => {});
    this.onPlaybackComplete = options.onPlaybackComplete || (() => {});

    this.continuousBuffer = new Float32Array(0);
    this.fullAudioBuffer = new Float32Array(0); // 保存完整音频数据用于重播
    this.playbackPosition = 0;

    this.smallChunksBuffer = [];
    this.smallChunksSize = 0;
    this.lastChunkTime = 0;
    this.lastDataTime = Date.now();

    this.previousFrame = new Float32Array(128);
    this.hasPreviousFrame = false;
  }

  /**
   * 初始化音频上下文
   */
  initAudioContext() {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      return this.audioContext;
    }

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext({
        latencyHint: "interactive",
        sampleRate: 22050,
      });

      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.volume;

      this.filterNode = this.audioContext.createBiquadFilter();
      this.filterNode.type = "lowpass";
      this.filterNode.frequency.value = this.filterFrequency;
      this.filterNode.Q.value = 0.7;

      this.scriptNode = this.audioContext.createScriptProcessor(1024, 1, 1);
      this.scriptNode.onaudioprocess = this.processAudio.bind(this);

      this.scriptNode.connect(this.filterNode);
      this.filterNode.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);

      return this.audioContext;
    } catch (err) {
      console.error("初始化音频上下文失败:", err);
      return null;
    }
  }

  /**
   * 开始播放
   */
  start() {
    if (this.isPlaying && !this.isPaused) return false;

    try {
      this.initAudioContext();

      if (this.audioContext.state === "suspended") {
        this.audioContext.resume();
      }

      this.isPlaying = true;
      this.isPaused = false;

      if (!this.scriptNode) {
        this.scriptNode = this.audioContext.createScriptProcessor(1024, 1, 1);
        this.scriptNode.onaudioprocess = this.processAudio.bind(this);
        this.scriptNode.connect(this.filterNode);
      }

      this.onStatusUpdate("正在播放");
      return true;
    } catch (err) {
      console.error("开始播放失败:", err);
      return false;
    }
  }

  /**
   * 暂停播放
   */
  pause() {
    if (!this.isPlaying || this.isPaused) return false;

    try {
      this.audioContext.suspend();
      this.isPaused = true;
      this.onStatusUpdate("已暂停");
      return true;
    } catch (err) {
      console.error("暂停播放失败:", err);
      return false;
    }
  }

  /**
   * 恢复播放
   */
  resume() {
    if (!this.isPlaying || !this.isPaused) return false;

    try {
      this.audioContext.resume();
      this.isPaused = false;
      this.onStatusUpdate("正在播放");
      return true;
    } catch (err) {
      console.error("恢复播放失败:", err);
      return false;
    }
  }

  /**
   * 停止播放
   */
  stop() {
    try {
      if (this.scriptNode) {
        this.scriptNode.disconnect();
        this.scriptNode = null;
      }

      // 关闭音频上下文
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
        this.gainNode = null;
        this.filterNode = null;
      }

      this.isPlaying = false;
      this.isPaused = false;
      this.isCompleted = false;
      this.playbackPosition = 0;

      this.continuousBuffer = new Float32Array(0);
      this.fullAudioBuffer = new Float32Array(0); // 同时清空完整音频缓冲区
      this.smallChunksBuffer = [];
      this.smallChunksSize = 0;
      this.hasPreviousFrame = false;
      this.lastDataTime = Date.now();

      this.onStatusUpdate("已停止");
      return true;
    } catch (err) {
      console.error("停止播放失败:", err);
      return false;
    }
  }

  /**
   * 重置播放位置但保留音频数据，用于重播
   */
  reset() {
    try {
      if (this.scriptNode) {
        this.scriptNode.disconnect();
        this.scriptNode = null;
      }

      this.isPlaying = false;
      this.isPaused = false;
      this.isCompleted = false;
      this.playbackPosition = 0;

      // 从完整音频缓冲区恢复数据，确保从头开始播放
      if (this.fullAudioBuffer.length > 0) {
        this.continuousBuffer = new Float32Array(this.fullAudioBuffer);
      }

      this.smallChunksBuffer = [];
      this.smallChunksSize = 0;
      this.hasPreviousFrame = false;
      this.lastDataTime = Date.now();

      this.onStatusUpdate("已重置");
      return true;
    } catch (err) {
      console.error("重置播放失败:", err);
      return false;
    }
  }

  /**
   * 添加PCM音频数据
   * @param {Float32Array} pcmData - PCM音频数据
   */
  addPCMData(pcmData) {
    if (!pcmData || pcmData.length === 0) return;

    try {
      const now = Date.now();
      this.lastDataTime = now;

      if (pcmData.length < this.smallChunkThreshold) {
        this.smallChunksBuffer.push(pcmData);
        this.smallChunksSize += pcmData.length;
        this.lastChunkTime = now;

        if (
          this.smallChunksSize >= this.smallChunkThreshold ||
          (this.smallChunksBuffer.length > 0 && now - this.lastChunkTime > 100)
        ) {
          this.processSmallChunks();
        }
      } else {
        if (this.smallChunksBuffer.length > 0) {
          this.processSmallChunks();
        }
        this.appendToContinuousBuffer(pcmData);
      }

      if (
        this.isPlaying &&
        !this.isPaused &&
        this.audioContext &&
        this.audioContext.state !== "running"
      ) {
        this.audioContext.resume();
      }
    } catch (err) {
      console.error(`添加PCM数据失败: ${err.message}`);
    }
  }

  /**
   * 处理小音频片段
   */
  processSmallChunks() {
    if (this.smallChunksBuffer.length === 0) return;

    try {
      const totalLength = this.smallChunksSize;
      const mergedData = new Float32Array(totalLength);

      let offset = 0;
      for (const chunk of this.smallChunksBuffer) {
        mergedData.set(chunk, offset);
        offset += chunk.length;
      }

      this.appendToContinuousBuffer(mergedData);

      this.smallChunksBuffer = [];
      this.smallChunksSize = 0;
    } catch (err) {
      console.error(`处理小音频片段失败: ${err.message}`);
    }
  }

  /**
   * 将PCM数据添加到连续缓冲区
   */
  appendToContinuousBuffer(pcmData) {
    // 更新播放缓冲区
    const newBuffer = new Float32Array(
      this.continuousBuffer.length + pcmData.length
    );
    newBuffer.set(this.continuousBuffer);
    newBuffer.set(pcmData, this.continuousBuffer.length);
    this.continuousBuffer = newBuffer;

    // 同时更新完整音频缓冲区用于重播
    const newFullBuffer = new Float32Array(
      this.fullAudioBuffer.length + pcmData.length
    );
    newFullBuffer.set(this.fullAudioBuffer);
    newFullBuffer.set(pcmData, this.fullAudioBuffer.length);
    this.fullAudioBuffer = newFullBuffer;

    if (!this.isPlaying) {
      this.start();
    }
  }

  /**
   * 音频处理回调
   */
  processAudio(audioProcessingEvent) {
    const outputBuffer = audioProcessingEvent.outputBuffer;
    const outputData = outputBuffer.getChannelData(0);
    const bufferSize = outputData.length;

    // 检查是否播放完成
    const now = Date.now();
    const noNewDataTimeout = 3000; // 3秒没有新数据认为播放完成
    const hasReceivedData = this.continuousBuffer.length > 0;
    const isPlaybackComplete =
      hasReceivedData &&
      this.playbackPosition >= this.continuousBuffer.length &&
      now - this.lastDataTime > noNewDataTimeout;

    if (isPlaybackComplete && !this.isCompleted) {
      this.isCompleted = true;
      this.onStatusUpdate("播放完成");
      this.onPlaybackComplete();
      // 停止播放
      for (let i = 0; i < bufferSize; i++) {
        outputData[i] = 0;
      }
      return;
    }

    if (
      this.playbackPosition >= this.continuousBuffer.length ||
      this.isPaused
    ) {
      for (let i = 0; i < bufferSize; i++) {
        outputData[i] = 0;
      }
      return;
    }

    const resampleRatio =
      this.sampleRate / this.audioContext.sampleRate / this.playbackRate;

    for (let i = 0; i < bufferSize; i++) {
      const srcIndex = this.playbackPosition + i * resampleRatio;
      const srcIndexInt = Math.floor(srcIndex);
      const fraction = srcIndex - srcIndexInt;

      if (srcIndexInt >= this.continuousBuffer.length - 1) {
        outputData[i] = 0;
        continue;
      }

      let sample =
        this.continuousBuffer[srcIndexInt] * (1 - fraction) +
        this.continuousBuffer[srcIndexInt + 1] * fraction;

      if (this.hasPreviousFrame) {
        const frameIndex = i % this.previousFrame.length;
        const smoothFactor = 0.1;
        sample =
          sample * (1 - smoothFactor) +
          this.previousFrame[frameIndex] * smoothFactor;
        this.previousFrame[frameIndex] = sample;
      } else {
        this.previousFrame[i % this.previousFrame.length] = sample;
        if (i === bufferSize - 1) {
          this.hasPreviousFrame = true;
        }
      }

      const fadeSamples = Math.floor(
        (this.fadeDuration * this.audioContext.sampleRate) / 1000
      );

      if (this.playbackPosition < fadeSamples) {
        const fadeInFactor = this.playbackPosition / fadeSamples;
        sample *= fadeInFactor;
      }

      const remainingSamples = this.continuousBuffer.length - srcIndexInt;
      if (remainingSamples < fadeSamples) {
        const fadeOutFactor = remainingSamples / fadeSamples;
        sample *= fadeOutFactor;
      }

      outputData[i] = sample * 0.95;
    }

    this.playbackPosition += bufferSize * resampleRatio;

    this.onStatusUpdate(
      `正在播放 (位置: ${Math.floor(this.playbackPosition)}/${
        this.continuousBuffer.length
      })`
    );
  }
}

/**
 * 音频数据处理工具函数
 */
class AudioUtils {
  /**
   * 将base64字符串转换为ArrayBuffer
   * @param {string} base64 - base64编码的字符串
   * @returns {ArrayBuffer} - 转换后的ArrayBuffer
   */
  static base64ToArrayBuffer(base64) {
    const binaryStr = atob(base64);
    const len = binaryStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * 解码PCM数据
   * @param {ArrayBuffer} arrayBuffer - 原始音频数据
   * @returns {Float32Array|null} - 解码后的PCM数据
   */
  static decodePcmData(arrayBuffer) {
    try {
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        console.warn("收到空的音频数据");
        return null;
      }

      const int16Array = new Int16Array(arrayBuffer);
      const floatArray = new Float32Array(int16Array.length);

      const compressionThreshold = 0.6;
      const compressionRatio = 0.7;

      for (let i = 0; i < int16Array.length; i++) {
        let sample = int16Array[i] / 32768.0;

        if (Math.abs(sample) > compressionThreshold) {
          const excess = Math.abs(sample) - compressionThreshold;
          const compression = excess * (1 - compressionRatio);
          sample =
            sample > 0
              ? compressionThreshold + compression
              : -compressionThreshold - compression;
        }

        floatArray[i] = sample * 0.85;
      }

      return floatArray;
    } catch (err) {
      console.error("解码 PCM 数据失败:", err);
      return null;
    }
  }
}

/**
 * 音频播放管理器
 * 提供简单的API来播放音频流
 */
class AudioStreamPlayer {
  constructor(options = {}) {
    this.player = new SimpleAudioPlayer({
      sampleRate: options.sampleRate || 16000,
      channels: options.channels || 1,
      volume: options.volume || 1,
      playbackRate: options.playbackRate || 1.0,
      fadeDuration: options.fadeDuration || 50,
      filterFrequency: options.filterFrequency || 8000,
      smallChunkThreshold: options.smallChunkThreshold || 5000,
      onStatusUpdate: options.onStatusUpdate || (() => {}),
      onPlaybackComplete: options.onPlaybackComplete || (() => {}),
    });
    
    // 为了兼容iOS，创建一个虚拟的audio元素用于静音控制
    this.audioElement = document.createElement('audio');
    this.audioElement.muted = false;
    this.audioElement.volume = 1;
  }

  /**
   * 播放base64编码的音频数据
   * @param {string} base64Audio - base64编码的音频数据
   */
  playBase64Audio(base64Audio) {
    if (!base64Audio) {
      console.warn("没有音频数据");
      return;
    }

    try {
      const arrayBuffer = AudioUtils.base64ToArrayBuffer(base64Audio);
      const pcmData = AudioUtils.decodePcmData(arrayBuffer);

      if (pcmData) {
        this.player.addPCMData(pcmData);
      }
    } catch (err) {
      console.error("播放音频失败:", err);
    }
  }

  /**
   * 播放PCM音频数据
   * @param {Float32Array} pcmData - PCM音频数据
   */
  playPCMData(pcmData) {
    if (!pcmData) {
      console.warn("没有PCM数据");
      return;
    }

    this.player.addPCMData(pcmData);
  }

  /**
   * 暂停播放
   */
  pause() {
    return this.player.pause();
  }

  /**
   * 恢复播放
   */
  resume() {
    return this.player.resume();
  }

  /**
   * 停止播放
   */
  stop() {
    this.player.stop();
  }

  /**
   * 设置静音状态
   * @param {boolean} muted - 是否静音
   */
  setMuted(muted) {
    if (this.audioElement) {
      this.audioElement.muted = muted;
    }
    // 同时控制Web Audio API的音量
    if (this.player && this.player.gainNode) {
      this.player.gainNode.gain.value = muted ? 0 : this.player.volume;
    }
  }

  /**
   * 获取静音状态
   */
  getMuted() {
    return this.audioElement ? this.audioElement.muted : false;
  }

  /**
   * 重播音频（从头开始播放已有的音频数据）
   */
  replay() {
    if (this.player.fullAudioBuffer && this.player.fullAudioBuffer.length > 0) {
      this.player.reset();
      this.player.start();
      return true;
    }
    return false;
  }

  /**
   * 检查是否有音频数据可以重播
   */
  hasAudioData() {
    return this.player.fullAudioBuffer && this.player.fullAudioBuffer.length > 0;
  }

  /**
   * 获取播放状态
   */
  getStatus() {
    return {
      isPlaying: this.player.isPlaying,
      isPaused: this.player.isPaused,
      isCompleted: this.player.isCompleted,
    };
  }
}

/**
 * 简化的一次性音频播放器
 * 专门用于发送消息后播放返回的音频内容
 */
class OneTimeAudioPlayer {
  constructor() {
    this.currentPlayer = null;
  }

  /**
   * 播放音频（如果有正在播放的音频会先停止）
   * @param {string} base64Audio - base64编码的音频数据
   * @param {Object} options - 配置选项
   */
  play(base64Audio, options = {}) {
    // 停止当前播放
    this.stop();

    // 创建新的播放器
    this.currentPlayer = new AudioStreamPlayer({
      volume: options.volume || 1,
      onStatusUpdate: options.onStatusUpdate || (() => {}),
      onPlaybackComplete: () => {
        if (options.onPlaybackComplete) {
          options.onPlaybackComplete();
        }
        // 播放完成后自动清理
        this.cleanup();
      },
    });

    // 开始播放
    this.currentPlayer.playBase64Audio(base64Audio);
  }

  /**
   * 停止当前播放
   */
  stop() {
    if (this.currentPlayer) {
      this.currentPlayer.stop();
      this.currentPlayer = null;
    }
  }

  /**
   * 清理资源
   */
  cleanup() {
    this.currentPlayer = null;
  }

  /**
   * 获取播放状态
   */
  isPlaying() {
    return this.currentPlayer
      ? this.currentPlayer.getStatus().isPlaying
      : false;
  }
}

// 创建全局实例
const globalAudioPlayer = new OneTimeAudioPlayer();

/**
 * 全局播放函数 - 最简单的使用方式
 * @param {string} base64Audio - base64编码的音频数据
 * @param {Object} options - 配置选项
 */
function playAudio(base64Audio, options = {}) {
  globalAudioPlayer.play(base64Audio, options);
}

/**
 * 停止全局播放
 */
function stopAudio() {
  globalAudioPlayer.stop();
}

export {
  AudioStreamPlayer,
  AudioUtils,
  SimpleAudioPlayer,
  OneTimeAudioPlayer,
  playAudio,
  stopAudio,
};
